import { MediaItem } from '../types';
import { ProcessedImage, ImageMetadata, ImageFormat, ImageSize, getSourceVersion } from './imageProcessingService';
import { storageService, STORES } from './storageService';
import { waitForTransaction } from './indexedDbTx';

// Use store names from storageService (single source of truth)
const STORE_PROCESSED_IMAGES = STORES.PROCESSED_IMAGES;
const STORE_IMAGE_METADATA = STORES.IMAGE_METADATA;

interface StoredProcessedImage {
  imageId: string;
  format: ImageFormat;
  size: ImageSize;
  blob: Blob;
  storedAt: number;
  sourceVersion: string;
}

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof indexedDB !== 'undefined';

/**
 * Image Storage Service
 * Stores processed images in IndexedDB with proper caching
 * 
 * NOTE: This service is a consumer of storageService - it does not manage
 * database schema. All schema is managed in storageService.ts
 */
export const imageStorageService = {
  /**
   * Open database - delegates to storageService (single schema owner)
   */
  async openDB(): Promise<IDBDatabase> {
    if (!isBrowser) {
      throw new Error('IndexedDB requires a browser environment');
    }
    return storageService.openDB();
  },

  /**
   * Save processed images for an item
   */
  async saveProcessedImages(
    item: MediaItem,
    processedImages: ProcessedImage[],
    sourceVersion: string = getSourceVersion(item)
  ): Promise<void> {
    if (!isBrowser) {
      console.warn('[imageStorageService] Cannot save processed images: not in browser environment');
      return;
    }
    const db = await this.openDB();
    
    // Defensive check
    if (!db.objectStoreNames.contains(STORE_PROCESSED_IMAGES)) {
      throw new Error(`Store ${STORE_PROCESSED_IMAGES} does not exist. Database may need upgrade.`);
    }
    
    const tx = db.transaction(STORE_PROCESSED_IMAGES, 'readwrite');
    const store = tx.objectStore(STORE_PROCESSED_IMAGES);

    // Delete existing processed images for this item
    const index = store.index('imageId');
    const request = index.openKeyCursor(IDBKeyRange.only(item.id));
    
    await new Promise<void>((resolve) => {
      request.onsuccess = (e: any) => {
        const cursor = e.target.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => resolve();
    });

    // Save new processed images
    for (const img of processedImages) {
      const stored: StoredProcessedImage = {
        imageId: item.id,
        format: img.format,
        size: img.size,
        blob: img.blob,
        storedAt: Date.now(),
        sourceVersion,
      };
      store.put(stored);
    }

    return waitForTransaction(tx);
  },

  /**
   * Get processed images for an item
   */
  async getProcessedImages(item: MediaItem): Promise<ProcessedImage[]> {
    if (!isBrowser) {
      console.warn('[imageStorageService] Cannot get processed images: not in browser environment');
      return [];
    }
    const db = await this.openDB();
    const sourceVersion = getSourceVersion(item);
    return new Promise((resolve) => {
      try {
        // Defensive check
        if (!db.objectStoreNames.contains(STORE_PROCESSED_IMAGES)) {
          console.warn(`[imageStorageService] Store ${STORE_PROCESSED_IMAGES} does not exist`);
          resolve([]);
          return;
        }
        
        const tx = db.transaction(STORE_PROCESSED_IMAGES, 'readonly');
        const store = tx.objectStore(STORE_PROCESSED_IMAGES);
        
        // Check if index exists
        if (!store.indexNames.contains('imageId')) {
          console.warn(`[imageStorageService] Index 'imageId' does not exist on ${STORE_PROCESSED_IMAGES}`);
          resolve([]);
          return;
        }
        
        const index = store.index('imageId');
        const request = index.getAll(item.id);
      
        request.onsuccess = () => {
          const stored = request.result as StoredProcessedImage[];
          const processed: ProcessedImage[] = stored
            .filter(s => s.sourceVersion === sourceVersion)
            .map(s => ({
              format: s.format,
              size: s.size,
              blob: s.blob,
              url: URL.createObjectURL(s.blob),
            }));
          resolve(processed);
        };
        
        request.onerror = () => resolve([]);
      } catch (err) {
        console.warn('[imageStorageService] Error getting processed images:', err);
        resolve([]);
      }
    });
  },

  /**
   * Get a specific processed image (format + size)
   */
  async getProcessedImage(
    item: MediaItem,
    format: ImageFormat,
    size: ImageSize,
    sourceVersion: string = getSourceVersion(item)
  ): Promise<ProcessedImage | null> {
    if (!isBrowser) {
      console.warn('[imageStorageService] Cannot get processed image: not in browser environment');
      return null;
    }
    const db = await this.openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_PROCESSED_IMAGES, 'readonly');
      const store = tx.objectStore(STORE_PROCESSED_IMAGES);
      const key = [item.id, format, size, sourceVersion];
      const request = store.get(key);
      
      request.onsuccess = () => {
        const stored = request.result as StoredProcessedImage | undefined;
        if (stored) {
          resolve({
            format: stored.format,
            size: stored.size,
            blob: stored.blob,
            url: URL.createObjectURL(stored.blob)
          });
        } else {
          resolve(null);
        }
      };
      
      request.onerror = () => resolve(null);
    });
  },

  /**
   * Save image metadata
   */
  async saveImageMetadata(item: MediaItem, metadata: ImageMetadata): Promise<void> {
    if (!isBrowser) {
      console.warn('[imageStorageService] Cannot save image metadata: not in browser environment');
      return;
    }
    const db = await this.openDB();
    
    // Defensive check
    if (!db.objectStoreNames.contains(STORE_IMAGE_METADATA)) {
      throw new Error(`Store ${STORE_IMAGE_METADATA} does not exist. Database may need upgrade.`);
    }
    
    const tx = db.transaction(STORE_IMAGE_METADATA, 'readwrite');
    const store = tx.objectStore(STORE_IMAGE_METADATA);
    store.put(metadata);
    return waitForTransaction(tx);
  },

  /**
   * Get image metadata
   */
  async getImageMetadata(item: MediaItem): Promise<ImageMetadata | null> {
    if (!isBrowser) {
      console.warn('[imageStorageService] Cannot get image metadata: not in browser environment');
      return null;
    }
    const db = await this.openDB();
    return new Promise((resolve) => {
      try {
        // Defensive check: ensure store exists
        if (!db.objectStoreNames.contains(STORE_IMAGE_METADATA)) {
          console.warn(`[imageStorageService] Store ${STORE_IMAGE_METADATA} does not exist`);
          resolve(null);
          return;
        }
        
        const tx = db.transaction(STORE_IMAGE_METADATA, 'readonly');
        const store = tx.objectStore(STORE_IMAGE_METADATA);
        const request = store.get(item.id);
        
        request.onsuccess = () => {
          resolve(request.result as ImageMetadata || null);
        };
        
        request.onerror = () => resolve(null);
      } catch (err) {
        console.warn('[imageStorageService] Error accessing image metadata:', err);
        resolve(null);
      }
    });
  },

  /**
   * Check if processed images exist for an item
   */
  async hasProcessedImages(item: MediaItem): Promise<boolean> {
    if (!isBrowser) {
      return false;
    }
    const metadata = await this.getImageMetadata(item);
    if (!metadata || metadata.formats.length === 0) {
      return false;
    }
    return metadata.sourceVersion === getSourceVersion(item);
  },

  /**
   * Clean up old processed images (older than 30 days)
   */
  async cleanupOldImages(): Promise<void> {
    if (!isBrowser) {
      console.warn('[imageStorageService] Cannot cleanup old images: not in browser environment');
      return;
    }
    const db = await this.openDB();
    const tx = db.transaction(STORE_PROCESSED_IMAGES, 'readwrite');
    const store = tx.objectStore(STORE_PROCESSED_IMAGES);
    const index = store.index('storedAt');
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    const request = index.openKeyCursor(IDBKeyRange.upperBound(thirtyDaysAgo));
    
    await new Promise<void>((resolve) => {
      request.onsuccess = (e: any) => {
        const cursor = e.target.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => resolve();
    });
    
    return waitForTransaction(tx);
  }
};
